import { For, Show, createResource, createSignal } from 'solid-js';
import { createStore } from 'solid-js/store';
import { useApi, isOk } from '../api';
import type { BotshopDomainDto, BotshopShopAssignmentDto } from '../api';
import Text from '../components/Text';
import { useTranslation } from '../hooks/useTranslation';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { EmptyState } from '../components/EmptyState';
import { SkeletonAuditRow } from '../components/Skeleton';
import styles from './AdminBotshopDomains.module.css';
import { firstError } from '../api/envelope';

const UNASSIGNED = '';

interface DomainForm {
    host: string;
    name: string;
    isEnabled: boolean;
    note: string;
}

const emptyForm = (): DomainForm => ({
    host: '',
    name: '',
    isEnabled: true,
    note: '',
});

export default function AdminBotshopDomains() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [reloadToken, setReloadToken] = createSignal(0);
    const [form, setForm] = createStore<DomainForm>(emptyForm());
    const [editingId, setEditingId] = createSignal<string | null>(null);
    const [submitting, setSubmitting] = createSignal(false);
    const [assigningId, setAssigningId] = createSignal<string | null>(null);

    const [domains] = createResource(
        reloadToken,
        async (): Promise<BotshopDomainDto[]> => {
            try {
                const res = await api.botshopDomainsAdmin.getDomains();
                if (isOk(res.data)) return res.data.value.domains ?? [];
            } catch { /* fall through to the toast */ }
            toast?.error(t('BOTSHOP_DOMAINS_FETCH_FAILED'));
            return [];
        }
    );

    const [shops] = createResource(
        reloadToken,
        async (): Promise<BotshopShopAssignmentDto[]> => {
            try {
                const res = await api.botshopDomainsAdmin.getShops();
                if (isOk(res.data)) return res.data.value.shops ?? [];
            } catch { /* fall through to the toast */ }
            toast?.error(t('BOTSHOP_SHOPS_FETCH_FAILED'));
            return [];
        }
    );

    const loading = () => domains.state === 'pending' || shops.state === 'pending';
    const reload = () => setReloadToken((n) => n + 1);

    const resetForm = () => {
        setForm(emptyForm());
        setEditingId(null);
    };

    const startEdit = (domain: BotshopDomainDto) => {
        setForm({
            host: domain.host ?? '',
            name: domain.name ?? '',
            isEnabled: domain.isEnabled ?? true,
            note: domain.note ?? '',
        });
        setEditingId(domain.id ?? null);
    };

    const submit = async () => {
        const host = form.host.trim();
        if (!host) {
            toast?.error(t('BOTSHOP_DOMAIN_HOST_REQUIRED'));
            return;
        }

        setSubmitting(true);
        try {
            const body = {
                host,
                name: form.name.trim(),
                isEnabled: form.isEnabled,
                note: form.note.trim() ? form.note.trim() : null,
            };

            const id = editingId();
            const res = id
                ? await api.botshopDomainsAdmin.updateDomain(id, body)
                : await api.botshopDomainsAdmin.createDomain(body);

            if (isOk(res.data)) {
                toast?.info(id ? t('BOTSHOP_DOMAIN_UPDATED') : t('BOTSHOP_DOMAIN_ADDED'));
                resetForm();
                reload();
            } else {
                // Keeps what was typed: the usual failure is a host already configured.
                toast?.error(firstError(res.data) ?? t('BOTSHOP_DOMAIN_SAVE_FAILED'));
            }
        } catch {
            toast?.error(t('BOTSHOP_DOMAIN_SAVE_FAILED'));
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (domain: BotshopDomainDto) => {
        if (!domain.id) return;
        const assigned = domain.assignedShopCount ?? 0;
        const question = assigned > 0
            ? t('BOTSHOP_DOMAIN_CONFIRM_DELETE_ASSIGNED', domain.host ?? '?', String(assigned))
            : t('BOTSHOP_DOMAIN_CONFIRM_DELETE', domain.host ?? '?');
        if (!await showConfirm(question, true)) return;

        try {
            const res = await api.botshopDomainsAdmin.deleteDomain(domain.id);
            if (isOk(res.data)) {
                toast?.info(t('BOTSHOP_DOMAIN_DELETED'));
                if (editingId() === domain.id) resetForm();
                reload();
            } else {
                toast?.error(t('BOTSHOP_DOMAIN_DELETE_FAILED'));
            }
        } catch {
            toast?.error(t('BOTSHOP_DOMAIN_DELETE_FAILED'));
        }
    };

    const assign = async (shop: BotshopShopAssignmentDto, domainId: string) => {
        if (!shop.sellbotConfigId) return;
        setAssigningId(shop.sellbotConfigId);
        try {
            const res = await api.botshopDomainsAdmin.assignShop(shop.sellbotConfigId, {
                domainId: domainId === UNASSIGNED ? null : domainId,
            });
            if (isOk(res.data)) {
                toast?.info(t('BOTSHOP_SHOP_ASSIGNED'));
                reload();
            } else {
                toast?.error(firstError(res.data) ?? t('BOTSHOP_SHOP_ASSIGN_FAILED'));
            }
        } catch {
            toast?.error(t('BOTSHOP_SHOP_ASSIGN_FAILED'));
        } finally {
            setAssigningId(null);
        }
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="BOTSHOP_DOMAINS_TITLE" /></h1>
                <span class={styles.entryCount}>
                    {(domains() ?? []).length} <Text val="BOTSHOP_DOMAINS_COUNT" />
                </span>
            </div>

            <p class={styles.intro}><Text val="BOTSHOP_DOMAINS_INTRO" /></p>

            <form class="fcard v" onSubmit={(e) => { e.preventDefault(); void submit(); }} style={{ gap: 'var(--spacing-md)' }}>
                <h2 class={styles.sectionTitle}>
                    <Text val={editingId() ? 'BOTSHOP_DOMAIN_EDIT' : 'BOTSHOP_DOMAIN_ADD'} />
                </h2>

                <div class={styles.formGrid}>
                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span class={styles.fieldLabel}><Text val="BOTSHOP_DOMAIN_FIELD_HOST" /></span>
                        <input
                            type="text"
                            value={form.host}
                            onInput={(e) => setForm('host', e.currentTarget.value)}
                            placeholder={t('BOTSHOP_DOMAIN_FIELD_HOST_PLACEHOLDER')}
                        />
                    </label>

                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span class={styles.fieldLabel}><Text val="BOTSHOP_DOMAIN_FIELD_NAME" /></span>
                        <input
                            type="text"
                            value={form.name}
                            onInput={(e) => setForm('name', e.currentTarget.value)}
                            placeholder={t('BOTSHOP_DOMAIN_FIELD_NAME_PLACEHOLDER')}
                        />
                    </label>

                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span class={styles.fieldLabel}><Text val="BOTSHOP_DOMAIN_FIELD_NOTE" /></span>
                        <input
                            type="text"
                            value={form.note}
                            onInput={(e) => setForm('note', e.currentTarget.value)}
                            placeholder={t('BOTSHOP_DOMAIN_FIELD_NOTE_PLACEHOLDER')}
                        />
                    </label>
                </div>

                <div class={styles.toggles}>
                    <label class={styles.toggle}>
                        <input type="checkbox" checked={form.isEnabled} onChange={(e) => setForm('isEnabled', e.currentTarget.checked)} />
                        <Text val="BOTSHOP_DOMAIN_FIELD_ENABLED" />
                    </label>
                </div>

                <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                    <Show when={editingId()}>
                        <button type="button" class="btn btn-secondary" onClick={resetForm}>
                            <Text val="CANCEL" />
                        </button>
                    </Show>
                    <button type="submit" class="btn btn-primary" disabled={submitting() || !form.host.trim()}>
                        <Show when={submitting()} fallback={<Text val={editingId() ? 'SAVE' : 'BOTSHOP_DOMAIN_ADD_ACTION'} />}>
                            <i class="fa-solid fa-spinner fa-spin" />
                        </Show>
                    </button>
                </div>
            </form>

            <Show when={loading()}>
                <div class="fcard v" style={{ gap: 'var(--spacing-sm)' }}>
                    <SkeletonAuditRow />
                    <SkeletonAuditRow />
                    <SkeletonAuditRow />
                </div>
            </Show>

            <Show when={!loading()}>
                <Show
                    when={(domains() ?? []).length > 0}
                    fallback={<EmptyState icon="fa-globe" title={t('BOTSHOP_DOMAINS_EMPTY')} hint={t('BOTSHOP_DOMAINS_EMPTY_HINT')} />}
                >
                    <div class="fcard">
                        <div class={styles.tableWrap}>
                            <table class={styles.table}>
                                <thead>
                                    <tr>
                                        <th><Text val="BOTSHOP_DOMAIN_COL_HOST" /></th>
                                        <th><Text val="BOTSHOP_DOMAIN_COL_NAME" /></th>
                                        <th><Text val="BOTSHOP_DOMAIN_COL_SHOPS" /></th>
                                        <th><Text val="BOTSHOP_DOMAIN_COL_STATE" /></th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    <For each={domains()}>{(domain) => (
                                        <tr>
                                            <td>
                                                <div class="v" style={{ gap: '2px' }}>
                                                    <span class={styles.host}>{domain.host}</span>
                                                    <Show when={domain.note}>
                                                        <span class={styles.note}>{domain.note}</span>
                                                    </Show>
                                                </div>
                                            </td>
                                            <td>{domain.name}</td>
                                            <td>{domain.assignedShopCount ?? 0}</td>
                                            <td>
                                                <span class={`${styles.pill} ${domain.isEnabled ? styles.pillOn : styles.pillOff}`}>
                                                    <Text val={domain.isEnabled ? 'BOTSHOP_DOMAIN_STATE_SERVING' : 'BOTSHOP_DOMAIN_STATE_OFF'} />
                                                </span>
                                            </td>
                                            <td>
                                                <div class={styles.rowActions}>
                                                    <button class="btn btn-secondary btn-sm" onClick={() => startEdit(domain)}>
                                                        <i class="fa-solid fa-pen-to-square" />
                                                    </button>
                                                    <button class="btn btn-danger btn-sm" onClick={() => void remove(domain)}>
                                                        <i class="fa-solid fa-trash" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}</For>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Show>

                <h2 class={styles.sectionTitle}><Text val="BOTSHOP_SHOPS_TITLE" /></h2>
                <p class={styles.intro}><Text val="BOTSHOP_SHOPS_INTRO" /></p>

                <Show
                    when={(shops() ?? []).length > 0}
                    fallback={<EmptyState icon="fa-store" title={t('BOTSHOP_SHOPS_EMPTY')} hint={t('BOTSHOP_SHOPS_EMPTY_HINT')} />}
                >
                    <div class="fcard">
                        <div class={styles.tableWrap}>
                            <table class={styles.table}>
                                <thead>
                                    <tr>
                                        <th><Text val="BOTSHOP_SHOP_COL_BOT" /></th>
                                        <th><Text val="BOTSHOP_SHOP_COL_OWNER" /></th>
                                        <th><Text val="BOTSHOP_SHOP_COL_READY" /></th>
                                        <th><Text val="BOTSHOP_SHOP_COL_DOMAIN" /></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <For each={shops()}>{(shop) => (
                                        <tr>
                                            <td><span class={styles.host}>{shop.botUsername}</span></td>
                                            <td>{shop.ownerUsername}</td>
                                            <td>
                                                <div class="h" style={{ gap: 'var(--spacing-sm)', 'flex-wrap': 'wrap' }}>
                                                    <span class={`${styles.pill} ${shop.ownerHasFeature ? styles.pillOn : styles.pillOff}`}>
                                                        <Text val="BOTSHOP_SHOP_FLAG" />
                                                    </span>
                                                    <span class={`${styles.pill} ${shop.publishToGrieferUtils ? styles.pillOn : styles.pillOff}`}>
                                                        <Text val="BOTSHOP_SHOP_PUBLISHING" />
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <select
                                                    class={styles.domainSelect}
                                                    disabled={assigningId() === shop.sellbotConfigId}
                                                    value={shop.botshopDomainId ?? UNASSIGNED}
                                                    onChange={(e) => void assign(shop, e.currentTarget.value)}
                                                >
                                                    <option value={UNASSIGNED}>{t('BOTSHOP_SHOP_UNASSIGNED')}</option>
                                                    <For each={domains()}>{(domain) => (
                                                        <option value={domain.id}>{domain.host}</option>
                                                    )}</For>
                                                </select>
                                            </td>
                                        </tr>
                                    )}</For>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
